import React, { useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  Smile,
  Heading2,
  Heading3,
  Undo,
  Redo,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Strikethrough,
} from 'lucide-react';
import { EmojiPicker } from './EmojiPicker';
import api from '../services/api';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  simple?: boolean;
  /** Geen linkknop, bijv. waar de server links weghaalt (jaarverslagteksten). */
  zonderLinks?: boolean;
}

const ToolbarButton: React.FC<{
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onMouseDown={(e) => {
      e.preventDefault();
      onClick();
    }}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded transition-colors text-sm ${
      active
        ? 'bg-[#0B1F3A] text-white'
        : disabled
        ? 'text-gray-300 cursor-not-allowed'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`}
  >
    {children}
  </button>
);

// Portal-based emoji picker: renders at fixed screen position, escapes all CSS stacking
const EmojiPickerPortal: React.FC<{
  anchorEl: HTMLElement | null;
  onSelect: (emoji: string) => void;
  onClose: () => void;
}> = ({ anchorEl, onSelect, onClose }) => {
  if (!anchorEl) return null;

  const rect = anchorEl.getBoundingClientRect();
  const pickerWidth = 288;
  const left = Math.min(rect.left, window.innerWidth - pickerWidth - 8);
  const top = rect.bottom + 4 + window.scrollY;

  return ReactDOM.createPortal(
    <EmojiPicker
      style={{ position: 'absolute', left, top, zIndex: 9999 }}
      onSelect={onSelect}
      onClose={onClose}
    />,
    document.body
  );
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  placeholder,
  minHeight = 200,
  simple = false,
  zonderLinks = false,
}) => {
  const { t } = useTranslation('common');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const resolvedPlaceholder = placeholder ?? t('editor.placeholder');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-[#0B1F3A] underline hover:text-[#1A3459]',
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full rounded-lg my-2',
        },
      }),
      Placeholder.configure({ placeholder: resolvedPlaceholder }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const insertEmoji = useCallback(
    (emoji: string) => {
      if (!editor) return;
      editor.chain().focus().insertContent(emoji).run();
    },
    [editor]
  );

  const applyLink = useCallback(() => {
    if (!editor) return;
    if (!linkUrl) {
      editor.chain().focus().unsetLink().run();
    } else {
      const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
      editor.chain().focus().setLink({ href: url }).run();
    }
    setShowLinkDialog(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const handleImageUpload = useCallback(
    async (file: File) => {
      if (!editor) return;
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('image', file);
        const res = await api.post('/community/upload-image', formData);
        const url: string = res.data.url;
        editor.chain().focus().setImage({ src: url }).run();
      } catch {
        // silent
      } finally {
        setUploading(false);
      }
    },
    [editor]
  );

  if (!editor) return null;

  const isLinkActive = editor.isActive('link');

  return (
    <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-[#0B1F3A] focus-within:border-transparent">
      {/* Toolbar */}
      <div className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200 rounded-t-lg relative">

        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title={t('editor.undo')}>
          <Undo className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title={t('editor.redo')}>
          <Redo className="w-3.5 h-3.5" />
        </ToolbarButton>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title={t('editor.bold')}>
          <Bold className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title={t('editor.italic')}>
          <Italic className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title={t('editor.underline')}>
          <UnderlineIcon className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title={t('editor.strikethrough')}>
          <Strikethrough className="w-3.5 h-3.5" />
        </ToolbarButton>

        {!simple && (
          <>
            <div className="w-px h-5 bg-gray-200 mx-1" />
            <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title={t('editor.heading2')}>
              <Heading2 className="w-3.5 h-3.5" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title={t('editor.heading3')}>
              <Heading3 className="w-3.5 h-3.5" />
            </ToolbarButton>
            <div className="w-px h-5 bg-gray-200 mx-1" />
            <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title={t('editor.alignLeft')}>
              <AlignLeft className="w-3.5 h-3.5" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title={t('editor.alignCenter')}>
              <AlignCenter className="w-3.5 h-3.5" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title={t('editor.alignRight')}>
              <AlignRight className="w-3.5 h-3.5" />
            </ToolbarButton>
            <div className="w-px h-5 bg-gray-200 mx-1" />
          </>
        )}

        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title={t('editor.bulletList')}>
          <List className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title={t('editor.orderedList')}>
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolbarButton>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* Link */}
        {!zonderLinks && (
          <div className="relative">
            <ToolbarButton
              onClick={() => {
                const prev = editor.getAttributes('link').href || '';
                setLinkUrl(prev);
                setShowLinkDialog((v) => !v);
              }}
              active={isLinkActive}
              title={t('editor.insertLink')}
            >
              <LinkIcon className="w-3.5 h-3.5" />
            </ToolbarButton>
            {showLinkDialog && (
              <div className="absolute z-50 top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-72">
                <p className="text-xs font-medium text-gray-700 mb-2">{t('editor.enterUrl')}</p>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applyLink();
                    if (e.key === 'Escape') setShowLinkDialog(false);
                  }}
                  placeholder="https://..."
                  autoFocus
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B1F3A]"
                />
                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={applyLink} className="flex-1 bg-[#0B1F3A] text-white text-xs rounded px-2 py-1.5 hover:bg-[#1A3459]">
                    {linkUrl ? t('editor.insert') : t('editor.remove')}
                  </button>
                  <button type="button" onClick={() => setShowLinkDialog(false)} className="flex-1 border border-gray-300 text-xs rounded px-2 py-1.5 hover:bg-gray-50">
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Image upload */}
        {!simple && (
          <>
            <ToolbarButton onClick={() => fileInputRef.current?.click()} disabled={uploading} title={t('editor.insertImage')}>
              <ImageIcon className="w-3.5 h-3.5" />
            </ToolbarButton>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImageUpload(file);
                e.target.value = '';
              }}
            />
          </>
        )}

        {/* Emoji button */}
        <button
          ref={emojiButtonRef}
          type="button"
          title={t('editor.insertEmoji')}
          onMouseDown={(e) => {
            e.preventDefault();
            setShowEmojiPicker((v) => !v);
          }}
          className={`p-1.5 rounded transition-colors text-sm ${
            showEmojiPicker
              ? 'bg-[#0B1F3A] text-white'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
          }`}
        >
          <Smile className="w-3.5 h-3.5" />
        </button>

        {showEmojiPicker && (
          <EmojiPickerPortal
            anchorEl={emojiButtonRef.current}
            onSelect={(emoji) => {
              insertEmoji(emoji);
              setShowEmojiPicker(false);
            }}
            onClose={() => setShowEmojiPicker(false)}
          />
        )}
      </div>

      {/* Editor content area */}
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none px-3 py-3 text-gray-800 focus:outline-none overflow-auto rounded-b-lg"
        style={{ minHeight }}
      />

      {uploading && (
        <div className="px-3 py-1.5 bg-blue-50 border-t border-blue-100 text-xs text-blue-600 rounded-b-lg">
          {t('editor.uploadingImage')}
        </div>
      )}
    </div>
  );
};
